import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import {
  ApplicationInsights,
  IEventTelemetry,
  IExceptionTelemetry,
  IPageViewTelemetry,
  ITelemetryItem,
} from '@microsoft/applicationinsights-web';
import { AnalyticsStrategy } from 'wflow-cdk';
import { AuthService } from 'wflow-main-app/src/app/auth/auth.service';
import { getOrg } from 'wflow-main-app/src/app/shared/utils/route.utils';
import { environment } from 'wflow-main-app/src/environments';

@Injectable({
  providedIn: 'root',
})
export class AzureAnalyticsService implements AnalyticsStrategy {
  private readonly user = toSignal(inject(AuthService).userData$);
  private readonly enable = !!environment.insightsInstrumentationKey;
  private readonly appInsights: ApplicationInsights;

  private readonly _customTelemetryInitializer = (
    item: ITelemetryItem,
  ): boolean | void => {
    const telemetryItem = item || { data: {} };
    telemetryItem.data = telemetryItem?.data || {};
    telemetryItem.data['userEmail'] = this.user()?.email ?? null;
    telemetryItem.data['orgId'] = getOrg() ?? null;
  };

  constructor() {
    if (this.enable) {
      const angularPlugin = new AngularPlugin();
      this.appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: environment.insightsInstrumentationKey,
          extensions: [angularPlugin as any],

          disableAjaxTracking: true,
          disableFetchTracking: true,
          disableExceptionTracking: true,
          disableCorrelationHeaders: true,
          disableTelemetry: false,
          autoTrackPageVisitTime: false,
          enableAutoRouteTracking: false,
          isBrowserLinkTrackingEnabled: false,
          disableCookiesUsage: true,
        },
      });

      this.appInsights.addTelemetryInitializer(
        this._customTelemetryInitializer,
      );
      this.appInsights.loadAppInsights();
    }
  }

  trackEvent(event: IEventTelemetry): void {
    if (!this.enable) {
      return;
    }

    this.appInsights.trackEvent(event);
  }

  trackPageView(pageView: IPageViewTelemetry): void {
    if (!this.enable) {
      return;
    }

    this.appInsights.trackPageView(pageView);
  }

  trackException(
    exception: IExceptionTelemetry,
    customProperties?: { [key: string]: any },
  ): void {
    if (!this.enable) {
      return;
    }

    this.appInsights.trackException(exception, customProperties);
  }
}
